<template>
    <Transition name="slide">
        <div v-if="canShow" class="box schedule-selection-action-bar">
            <div class="action-bar-content">
                <button class="button is-light" @click="handleClearSelection">
                    <span class="icon">
                        <i class="fas fa-times"></i>
                    </span>
                    <span>{{ translate("admin_schedule_manage.clear_selection") }}</span>
                </button>

                <button v-if="canDistribute" class="button" @click="openDistributeModal">
                    <span class="icon">
                        <i class="fas fa-random"></i>
                    </span>
                    <span> {{ translate("admin_schedule_manage.distribute") }} </span>
                </button>

                <button v-if="canConfigureSlicing" class="button" @click="openSlicingConfig">
                    <span class="icon">
                        <i class="fas fa-cut"></i>
                    </span>
                    <span>{{ translate("admin_schedule_manage.slice") }}</span>
                </button>

                <button v-if="canMergeBack" class="button" @click="mergeBack">
                    <span class="icon">
                        <i class="fas fa-compress-alt"></i>
                    </span>
                    <span>{{ translate("admin_schedule_manage.merge_back") }}</span>
                </button>

                <button v-if="canMoveToDropzone" class="button" @click="handleMoveToDropzone">
                    <span class="icon">
                        <i class="fas fa-undo"></i>
                    </span>
                    <span>
                        {{ translate("admin_schedule_manage.move_to_unscheduled") }}
                    </span>
                </button>
            </div>
        </div>
    </Transition>
</template>

<script setup lang="ts">
    import { computed } from "vue";
    import { translate } from "../../Common/translations";
    import { useShowScheduleStore } from "./useShowScheduleStore";
    import { useScheduleMainStore } from "./useScheduleMainStore";
    import { eventBus, ON_DISTRIBUTE_TOOL_MODAL_OPENED, ON_SLICE_TOOL_MODAL_OPENED } from "./eventBus";
    import { handleError } from "../../Common/errorHandler";
    import schedulesApi from "../../Api/schedules";
    import { successToast } from "../../Common/toasts";
    import { canConfigureBracketSlicing, type BracketSlicingTarget, type DropZoneItem } from "./dropZoneItems";

    // Stores
    const showScheduleStore = useShowScheduleStore();
    const mainStore = useScheduleMainStore();

    // Computed
    const canShow = computed(() => showScheduleStore.selectedItems.size > 0);

    const canMoveToDropzone = computed(() => {
        return showScheduleStore.activeSelectionContext?.type === "mat";
    });
    const canDistribute = computed(() => {
        return showScheduleStore.activeSelectionContext?.type === "dropzone";
    });
    const selectedDropZoneBracketItem = computed<DropZoneItem | null>(() => {
        const selectedItems = showScheduleStore.findSelectedDropZoneItems();
        const firstItem = selectedItems[0];

        if (!firstItem) {
            return null;
        }

        const hasOneBracketSelected = selectedItems.every(item => item.parameterId === firstItem.parameterId);

        return hasOneBracketSelected ? firstItem : null;
    });
    const canConfigureSlicing = computed(() => {
        const selectedItem = selectedDropZoneBracketItem.value;

        if (!selectedItem) {
            return false;
        }

        return canConfigureBracketSlicing(selectedItem);
    });
    const canMergeBack = computed(() => {
        const selectedItem = selectedDropZoneBracketItem.value;

        if (!selectedItem || !selectedItem.hasSchedulingConfig) {
            return false;
        }

        return !showScheduleStore
            .getAllScheduledCategories()
            .some(scheduledCategory => scheduledCategory.parameterId === selectedItem.parameterId);
    });

    // Methods
    function handleMoveToDropzone() {
        showScheduleStore.moveSelectedToDropzone().catch(handleError);
    }

    function handleClearSelection() {
        showScheduleStore.clearSelection();
    }

    function openDistributeModal() {
        eventBus.$emit(ON_DISTRIBUTE_TOOL_MODAL_OPENED);
    }

    function openSlicingConfig() {
        const selectedItem = selectedDropZoneBracketItem.value;

        if (!selectedItem) {
            return;
        }

        const target: BracketSlicingTarget = {
            parameter_id: selectedItem.parameterId,
            name: selectedItem.name,
            total_real_competitors: selectedItem.totalRealCompetitors,
            bracketType: selectedItem.bracketType,
            schedulingConfig: selectedItem.schedulingConfig,
            supportsRoundSlicing: selectedItem.supportsRoundSlicing,
        };

        eventBus.$emit(ON_SLICE_TOOL_MODAL_OPENED, target);
    }

    function mergeBack() {
        const selectedItem = selectedDropZoneBracketItem.value;

        if (!selectedItem) {
            return;
        }

        mainStore
            .trackRequest(schedulesApi.deleteBracketSchedulingConfig(mainStore.eventId, selectedItem.parameterId))
            .then(() => {
                successToast(translate("admin_schedule_manage.slicing_merged"));
                showScheduleStore.clearSelection();
                return mainStore.refreshDropZoneItems();
            })
            .catch(handleError);
    }
</script>
